// intum_controller.js
import { Controller } from "@hotwired/stimulus"
import hotkeys from "hotkeys-js"

/**
 * Uniwersalny controller Intum - toggle, move, focus + keyboard shortcuts
 *
 * @example Toggle - pokazuj/ukrywaj elementy
 * <button data-controller="intum"
 *         data-action="click->intum#toggle"
 *         data-intum-show="modal"
 *         data-intum-hide="menu">
 *   Otwórz modal
 * </button>
 *
 * @example Move - przenoś elementy tam i z powrotem
 * <button data-controller="intum"
 *         data-action="click->intum#move"
 *         data-intum-element="widget"
 *         data-intum-target="sidebar">
 *   Przenieś widget
 * </button>
 *
 * @example Focus - ustaw focus na elemencie (kursor na końcu tekstu)
 * <button data-controller="intum"
 *         data-action="click->intum#focus"
 *         data-intum-focus="searchInput"
 *         data-intum-delay="100">
 *   Fokus na search
 * </button>
 *
 * @example Keyboard shortcuts - dodaj skróty klawiszowe
 * <button data-controller="intum"
 *         data-intum-shortcut="/"
 *         data-action="click->intum#toggle click->intum#focus"
 *         data-intum-show="searchModal"
 *         data-intum-focus="searchInput">
 *   Szukaj (/)
 * </button>
 *
 * @example Escape zamyka modal
 * <button data-controller="intum"
 *         data-intum-shortcut="esc"
 *         data-action="click->intum#toggle"
 *         data-intum-hide="modal, backdrop"
 *         class="hidden">
 * </button>
 *
 * @example Ctrl+K command palette
 * <button data-controller="intum"
 *         data-intum-shortcut="ctrl+k,command+k"
 *         data-action="click->intum#toggle"
 *         data-intum-show="commandPalette">
 *   Commands (Ctrl+K)
 * </button>
 *
 * @example Kombinacja akcji - toggle + focus
 * <button data-controller="intum"
 *         data-intum-shortcut="/"
 *         data-action="click->intum#toggle click->intum#focus"
 *         data-intum-show="search, backdrop"
 *         data-intum-focus="searchInput"
 *         data-intum-delay="100">
 *   Wyszukaj (/)
 * </button>
 *
 * @example Sekwencje klawiszy (jak w Vim)
 * <button data-controller="intum"
 *         data-intum-shortcut="g h"
 *         data-action="click->intum#toggle"
 *         data-intum-show="home">
 *   Home (g h)
 * </button>
 *
 * @example Własna klasa do ukrywania
 * <div data-controller="intum"
 *      data-intum-hidden-class-value="opacity-0 invisible">
 *   <button data-action="click->intum#toggle"
 *           data-intum-toggle="alert">
 *     Toggle
 *   </button>
 * </div>
 *
 * @example Scope dla kontekstowych skrótów
 * <button data-controller="intum"
 *         data-intum-shortcut="ctrl+b"
 *         data-intum-scope="editor"
 *         data-action="click->intum#toggle"
 *         data-intum-show="formatMenu">
 *   Bold (Ctrl+B)
 * </button>
 *
 * @example Wiele shortcutów w jednym kontrolerze (Stimulus Targets - wydajniejsze)
 * <div data-controller="intum">
 *   <button data-intum-target="shortcut"
 *           data-intum-shortcut="ctrl+n"
 *           data-action="click->intum#toggle"
 *           data-intum-show="newModal">
 *     Nowy (Ctrl+N)
 *   </button>
 *   <button data-intum-target="shortcut"
 *           data-intum-shortcut="ctrl+e"
 *           data-action="click->intum#toggle"
 *           data-intum-show="editModal">
 *     Edytuj (Ctrl+E)
 *   </button>
 *   <button data-intum-target="shortcut"
 *           data-intum-shortcut="ctrl+d"
 *           data-action="click->intum#toggle"
 *           data-intum-show="deleteModal">
 *     Usuń (Ctrl+D)
 *   </button>
 * </div>
 *
 * @example Popularne skróty klawiszowe:
 * "/" - wyszukiwarka
 * "?" - pomoc/shortcuts
 * "esc" - zamknij modal
 * "ctrl+k" lub "command+k" - command palette
 * "ctrl+b" - toggle sidebar
 * "ctrl+n" - nowy element
 * "g h" - go home
 * "g d" - go dashboard
 *
 * @param {string} data-intum-show - ID elementów do pokazania (oddzielone przecinkami)
 * @param {string} data-intum-hide - ID elementów do ukrycia (oddzielone przecinkami)
 * @param {string} data-intum-toggle - ID elementów do przełączenia (oddzielone przecinkami)
 * @param {string} data-intum-element - ID elementu do przeniesienia
 * @param {string} data-intum-focus - ID elementu do ustawienia focus
 * @param {string} data-intum-target - ID kontenera docelowego dla move lub "shortcut" dla oznaczenia elementów ze shortcuts
 * @param {string} data-intum-delay - Opóźnienie w ms dla focus
 * @param {string} data-intum-shortcut - Skrót klawiszowy (hotkeys-js format)
 * @param {string} data-intum-scope - Scope dla hotkeys (domyślnie "all")
 * @param {string} data-intum-hidden-class-value - Klasa CSS do ukrywania (domyślnie "hidden")
 *
 * @note Dla wydajności: używaj data-intum-target="shortcut" na elementach z shortcuts (cache'owane przez Stimulus)
 */

// Connects to data-controller="intum"
export default class extends Controller {
  static values = {
    hiddenClass: { type: String, default: "hidden" }
  }

  static targets = ["shortcut"]

  connect() {
    // Dla akcji move
    this.moveStates = new Map()

    // Lista zarejestrowanych hotkeys
    this.registeredHotkeys = []

    // Konfiguruj hotkeys
    this.setupHotkeys()
  }

  disconnect() {
    // Odrejestruj wszystkie zarejestrowane skróty
    this.registeredHotkeys.forEach(({ shortcut, scope }) => {
      hotkeys.unbind(shortcut, scope)
    })
  }

  setupHotkeys() {
    // Użyj Stimulus targets (szybsze niż querySelectorAll, bo są cache'owane)
    // Jeśli nie ma targets, sprawdź główny element
    const elementsWithShortcuts = this.hasShortcutTarget
      ? this.shortcutTargets
      : (this.element.dataset.intumShortcut ? [this.element] : [])

    // Dla każdego elementu zarejestruj jego shortcut
    elementsWithShortcuts.forEach(element => {
      const shortcut = element.dataset.intumShortcut
      if (!shortcut) return

      const scope = element.dataset.intumScope || "all"

      // Ustaw scope
      hotkeys.setScope(scope)

      // Dodaj title ze skrótem jeśli element nie ma title
      if (!element.hasAttribute("title")) {
        element.setAttribute("title", shortcut)
      }

      // Zarejestruj skrót
      hotkeys(shortcut, scope, (event, _handler) => {
        // Ignoruj skrót gdy focus jest w input/textarea/select (chyba że to contenteditable)
        const target = event.target
        const tagName = target.tagName
        const isInput = tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT"
        const isContentEditable = target.contentEditable === "true"

        if (isInput && !isContentEditable) {
          return // Nie blokuj domyślnej akcji, pozwól wpisać znak
        }

        event.preventDefault()

        // Symuluj kliknięcie w odpowiedni element
        element.click()
      })

      // Zapisz do listy zarejestrowanych hotkeys
      this.registeredHotkeys.push({ shortcut, scope })
    })
  }

  // ============================================
  // TOGGLE - pokazuj/ukrywaj/przełączaj elementy
  // ============================================
  toggle(event) {
    const button = event.currentTarget

    const showIds = button.dataset.intumShow
    const hideIds = button.dataset.intumHide
    const toggleIds = button.dataset.intumToggle

    if (showIds) {
      this.showElements(showIds)
    }

    if (hideIds) {
      this.hideElements(hideIds)
    }

    if (toggleIds) {
      this.toggleElements(toggleIds)
    }
  }

  showElements(ids) {
    this.getElementsFromString(ids).forEach(element => {
      element.classList.remove(this.hiddenClassValue)
    })
  }

  hideElements(ids) {
    this.getElementsFromString(ids).forEach(element => {
      element.classList.add(this.hiddenClassValue)
    })
  }

  toggleElements(ids) {
    this.getElementsFromString(ids).forEach(element => {
      element.classList.toggle(this.hiddenClassValue)
    })
  }

  getElementsFromString(idsString) {
    if (!idsString) return []

    const ids = idsString.split(",").map(id => id.trim())
    const elements = []

    ids.forEach(id => {
      const element = document.getElementById(id)
      if (element) {
        elements.push(element)
      } else {
        console.warn(`[intum] Element with ID "${id}" not found`)
      }
    })

    return elements
  }

  // ============================================
  // MOVE - przenoś element tam i z powrotem
  // ============================================
  move(event) {
    const button = event.currentTarget

    const elementId = button.dataset.intumElement
    const targetId = button.dataset.intumTarget

    const element = document.getElementById(elementId)
    const target = document.getElementById(targetId)

    if (!element || !target) {
      console.error("[intum] Element or target not found", {
        element: elementId,
        target: targetId
      })
      return
    }

    if (!this.moveStates.has(elementId)) {
      this.moveStates.set(elementId, {
        originalParent: null,
        originalNextSibling: null,
        moved: false
      })
    }

    const state = this.moveStates.get(elementId)

    if (!state.moved) {
      state.originalParent = element.parentNode
      state.originalNextSibling = element.nextSibling

      target.appendChild(element)
      state.moved = true
    } else {
      if (state.originalNextSibling) {
        state.originalParent.insertBefore(element, state.originalNextSibling)
      } else {
        state.originalParent.appendChild(element)
      }
      state.moved = false
    }
  }

  // ============================================
  // SHOW/HIDE IF VALUE / CHECKED
  // na podstawie fakturownia firmlet-1.3.js: show_if_value, hide_if_value, show_if_checked
  //
  // @example show_if_value - pokaż element gdy select ma konkretną wartość
  // <select data-controller="intum"
  //         data-action="change->intum#showIfValue"
  //         data-intum-show="smtp_section"
  //         data-intum-if-value="own">
  //
  // @example hide_if_value - ukryj element gdy select ma konkretną wartość
  // <select data-action="change->intum#hideIfValue"
  //         data-intum-hide="basic_section"
  //         data-intum-if-value="none">
  //
  // @example show_if_checked - pokaż/ukryj element w zależności od checkboxa
  // <input type="checkbox" data-controller="intum"
  //        data-action="change->intum#showIfChecked"
  //        data-intum-show="details_section">
  // ============================================

  showIfValue(event) {
    const el = event.currentTarget
    const values = (el.dataset.intumIfValue || "").split(",").map(v => v.trim())
    const showIds = el.dataset.intumShow
    if (!showIds) return

    if (values.includes(el.value)) {
      this.showElements(showIds)
    } else {
      this.hideElements(showIds)
    }
  }

  hideIfValue(event) {
    const el = event.currentTarget
    const values = (el.dataset.intumIfValue || "").split(",").map(v => v.trim())
    const hideIds = el.dataset.intumHide
    if (!hideIds) return

    if (values.includes(el.value)) {
      this.hideElements(hideIds)
    } else {
      this.showElements(hideIds)
    }
  }

  showIfChecked(event) {
    const el = event.currentTarget
    const showIds = el.dataset.intumShow
    if (!showIds) return

    if (el.checked) {
      this.showElements(showIds)
    } else {
      this.hideElements(showIds)
    }
  }

  // ============================================
  // SELECT TO TEXT - zamienia select na text input po wybraniu specjalnej wartości
  // na podstawie fakturownia firmlet-1.3.js: select_to_text_field()
  //
  // @example
  // <select data-action="change->intum#selectToText"
  //         data-intum-if-value="other"
  //         data-intum-show="smtp_section"
  //         data-placeholder="domena.pl">
  // ============================================

  // data-intum-set-value="elementId:value" — ustaw wartość innego elementu i wyzwól change
  // uzyte np w mailboxes/_form.html.erb
  selectToText(event) {
    const select = event.currentTarget
    const triggerValue = select.dataset.intumIfValue || "other"
    if (select.value !== triggerValue) return

    const input = document.createElement("input")
    input.type = "text"
    input.id = select.id
    input.name = select.name
    input.className = select.className
    input.placeholder = select.dataset.placeholder || ""
    input.value = ""
    input.disabled = select.disabled

    select.replaceWith(input)
    input.focus()

    const showIds = select.dataset.intumShow
    if (showIds) this.showElements(showIds)

    const setValue = select.dataset.intumSetValue
    if (setValue) {
      const [targetId, value] = setValue.split(":")
      const target = document.getElementById(targetId)
      if (target) {
        target.value = value
        target.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
  }

  // ============================================
  // FOCUS - ustaw focus na elemencie (kursor na końcu)
  // ============================================
  focus(event) {
    const button = event.currentTarget
    const elementId = button.dataset.intumFocus
    const delay = button.dataset.intumDelay || 0

    const element = document.getElementById(elementId)

    if (!element) {
      console.warn(`[intum] Element with ID "${elementId}" not found`)
      return
    }

    const doFocus = () => {
      element.focus()

      // Jeśli to input/textarea, przenieś kursor na koniec
      if (element.value !== undefined) {
        const len = element.value.length
        element.setSelectionRange(len, len)
        element.scrollLeft = element.scrollWidth
      }
    }

    if (delay > 0) {
      setTimeout(doFocus, parseInt(delay))
    } else {
      doFocus()
    }
  }
}
